/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { ButtonComponent,
    CodeSnippetComponent, ScrollContainerComponent,
    LinkPanelComponent, ModalComponent, PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent, FormPasswordInputComponent, requiredValidator, FormComponent, FormActionsComponent, ModalCloseButtonComponent, FormInputComponent
} from "typedb-cloud-framework";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { SnackbarService } from "../../../../../service/snackbar.service";
import { Cluster, ClusterStatus } from "../../../../../concept/cluster";
import { combineLatest, filter, map, shareReplay } from "rxjs";
import { MatSelectModule } from "@angular/material/select";
import { MatStepperModule } from "@angular/material/stepper";
import { ConnectDialogData, copy, ParsedVersion, parseVersionString, PASSWORD_FILLER } from "../../shared";
import { ClusterLoadingTipsComponent } from "../loading/cluster-loading-tips.component";

@Component({
    selector: "tp-cluster-console-connect-dialog",
    templateUrl: "./cluster-console-connect-dialog.component.html",
    styleUrls: ["./cluster-console-connect-dialog.component.scss"],
    imports: [
        AsyncPipe, MatTooltipModule, MatButtonModule, MatSelectModule,
        CodeSnippetComponent, ScrollContainerComponent, MatStepperModule,
        MatDialogContent, MatDialogTitle, ModalCloseButtonComponent, ClusterLoadingTipsComponent
    ]
})
export class ClusterConsoleConnectDialogComponent {
    serverAddresses$ = combineLatest([this.data.servers$, this.data.cluster$]).pipe(
        filter(([servers, cluster]) => servers.every((server) => !!server.address) && servers.length == cluster.serverCount),
        map(([servers, _]) => servers.map((server) => server.address!)),
        shareReplay(1),
    );
    clusterStatus$ = this.data.cluster$.pipe(map((cluster) => cluster.status));
    ready$ = this.clusterStatus$.pipe(map((status) => status == "running"));

    private loadingStatuses: ClusterStatus[] = ["starting", "resuming"];
    loading$ = this.clusterStatus$.pipe(map((status) => this.loadingStatuses.includes(status)));

    parsedVersion$ = this.data.cluster$.pipe(map(cluster => parseVersionString(cluster.typeDBVersion.version)));
    isTypeDBVersion2$ = this.parsedVersion$.pipe(map(version => version.major == 2));

    docsVersionSpecifier$ = this.isTypeDBVersion2$.pipe(map((isVersion2) => isVersion2 ? "2.x/" : ""));
    consoleInstallDocsLink$ = this.docsVersionSpecifier$.pipe(map((specifier) => `https://typedb.com/docs/home/${specifier}install-tools#_console`));

    connectionCommand$ = combineLatest([this.serverAddresses$, this.parsedVersion$]).pipe(
        map(([addresses, version]) => {
            const version3_1OrLater = version.major >= 3 && version.minor >= 1;
            const tlsArg = version3_1OrLater ? '' : '--tls-enabled';

            const addressesArg = this.getAddressArgs(addresses, version);

            return `./typedb console ${addressesArg} --username=admin ${tlsArg}`
        }),
        shareReplay(1),
    );

    constructor(
        private dialogRef: MatDialogRef<ClusterConsoleConnectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConnectDialogData, protected snackbar: SnackbarService,
    ) { }

    clusterCredentialFilename(cluster: Cluster) {
        return `typedb-cloud-credentials-${cluster.id}.txt`
    }

    close() {
        this.dialogRef.close();
    }

    private getAddressArgs(addresses: string[], version: ParsedVersion): string {
        if (version.major < 3) {
            return addresses.map((x) => `--cloud='${x}'`).join(" ");
        } else if (addresses.length === 1) {
            const addressArg = version.major >= 3 && version.minor >= 1 ? 'address' : 'cloud'
            return `--${addressArg}='${addresses[0]}'`;
        } else {
            return `--addresses='${addresses.join(",")}'`;
        }
    }

    protected readonly copy = copy;
}
